/**
 * 08 个人中心 `/me`（底部导航第三项）
 * -----------------------------------------------------------------------------
 * 版面按设计稿「学员端 · 个人中心」：资料卡（头像 + 姓名 + 脱敏手机号 + 批次徽标）
 * → 账号信息 → 快捷入口 → 底部导航。
 *
 * 设计稿里的「常用邮箱」与服务端未提供读取端点的「收件地址」不落字段：
 * 一期接口只有 `PUT …/address` 写入，没有读取，凭本地缓存伪造区域地址会换设备即丢
 * （与 `ServiceHelp` 的区域客服码同一类取舍），故只保留可读可改的项。
 */

import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal, Toast } from 'antd-mobile';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { describeApiError } from '../api/errors';
import { changeMyPhone, sendPhoneCode } from '../api/h5';
import { h5QueryKeys, useEntryCapture, useMyRegistrations, useSession } from '../api/hooks';
import { CaptchaModal, type CaptchaSubmit } from '../components/CaptchaModal';
import { ListCard, ListRow, PageShell } from '../components/layout';
import { maskPhone } from '../lib/phone-format';
import { useSendCooldown } from '../lib/send-cooldown';
import { useDismissBackGuard } from '../lib/use-dismiss-back-guard';
import { lastRegistrationId } from '../store/recent';

const MOBILE_PATTERN = /^1[3-9]\d{9}$/;

/** 自助改手机号（附件1：改号前须验证**新号**，一步完成校验 + 打免验标记） */
function ChangePhoneModal({
  visible,
  onClose,
  onDone,
}: {
  visible: boolean;
  onClose: () => void;
  onDone: (phoneMasked: string) => void;
}) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  // 发码倒计时与号码无关，且**关掉弹窗再打开也不解锁** —— 否则「关闭→打开」
  // 就是一条免费重发通道，正好绕过服务端按号码算的 60s 冷却（见 lib/send-cooldown）
  const { remaining: cooldown, markSent } = useSendCooldown();
  const [busy, setBusy] = useState(false);
  /** 图形码浮层（2026-10-27）：服务端发码限流按号码隔离，换号即可绕过，必须过人机校验 */
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const phoneOk = MOBILE_PATTERN.test(phone.trim());

  // 弹窗占一层返回守卫：全面屏右滑 / 安卓返回键 = **关掉改号弹窗**，页面不动。
  useDismissBackGuard(visible, onClose);

  // 关闭即清空（新号验证码不该留到下一次）。
  // 注意**不**清倒计时：锁是跨弹窗生命周期的，清掉就等于「关掉再打开」免费重发一次。
  useEffect(() => {
    if (!visible) {
      setPhone('');
      setCode('');
      setCaptchaVisible(false);
    }
  }, [visible]);

  /**
   * 浮层交来凭据后发码。**失败直接抛给浮层**（它负责换图 + 提示），
   * 这里只管成功收尾 —— 再 `catch` 一次就会出现两句提示。
   */
  const send = async ({ captchaId, captchaCode }: CaptchaSubmit) => {
    const result = await sendPhoneCode(phone.trim(), { captchaId, captchaCode });
    // 用服务端回传的时长落锁（口径只有一处），取不到时模块内部退回 60s
    markSent(result.cooldownSeconds);
    Toast.show({ icon: 'success', content: `验证码已发送至 ${maskPhone(phone.trim())}` });
  };

  const save = async () => {
    if (!phoneOk || code.trim().length !== 6 || busy) return;
    setBusy(true);
    try {
      const result = await changeMyPhone(phone.trim(), code.trim());
      Toast.show({ icon: 'success', content: '手机号已更新' });
      onDone(result.phoneMasked);
      onClose();
    } catch (error) {
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        visible={visible}
        title="修改手机号"
        onClose={onClose}
        closeOnMaskClick
        bodyStyle={{ paddingTop: 12 }}
        content={
          <div className="change-phone-body">
            <div className="field-box">
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={11}
                value={phone}
                placeholder="新手机号"
                onChange={setPhone}
              />
            </div>
            <div className="phone-verify-row">
              <div className="field-box phone-verify-field">
                <Input
                  value={code}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6 位验证码"
                  disabled={!phoneOk}
                  onChange={setCode}
                />
              </div>
              <Button
                size="small"
                fill="outline"
                disabled={!phoneOk || cooldown > 0 || busy}
                onClick={() => setCaptchaVisible(true)}
              >
                {cooldown > 0 ? `${cooldown}s 后重发` : '发送验证码'}
              </Button>
            </div>
            <div className="phone-verify-hint">验证码发送至新手机号，验证通过即完成改绑。</div>
          </div>
        }
        actions={[
          { key: 'cancel', text: '取消', onClick: onClose },
          {
            key: 'save',
            text: busy ? '提交中…' : '确认修改',
            primary: true,
            disabled: !phoneOk || code.trim().length !== 6 || busy,
            onClick: save,
          },
        ]}
      />

      {/* 图形码浮层叠在改号弹窗之上：返回手势是栈式的，先关本层，改号弹窗不动 */}
      <CaptchaModal
        visible={captchaVisible}
        onClose={() => setCaptchaVisible(false)}
        onSubmit={send}
      />
    </>
  );
}

export default function Me() {
  useEntryCapture();
  const navigate = useNavigate();
  const session = useSession();
  const mine = useMyRegistrations();
  const queryClient = useQueryClient();
  const recentId = lastRegistrationId();
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  const latest = mine.data?.items?.[0];
  const phoneMasked = session.data?.phoneMasked ?? null;
  /**
   * 资料卡称呼：**账号级姓名**（`users.realName`）。
   *
   * 2026-10-31 改口径。原先取「最近一份报名单」的 `applicantName`（列表按 createdAt
   * 倒序取第一条），有三个说不通的副作用：代报场景显示的是被代报人姓名；跨批次复报
   * 姓名随最新单跳变；新建草稿（姓名还没填）会立刻把真名顶成空。
   *
   * 现在姓名是**账号属性**，由 `/profile/complete` 强制填写页写入、个人中心可改，
   * 与报名单脱钩。`Registration.applicantName` 仍是报名单级权威字段（代报可指向别人、
   * 后台档案按它检索），只是不再作为个人中心的显示来源。
   *
   * 微信昵称仍不作兜底：授权用 `snsapi_base`，只回 openid、不返回昵称与头像
   * （见 `wechat-auth.ts` 的 `WechatSession`），`nickname` 通常为空。头像同理，
   * 用姓名首字占位，绝不复用证件照 / 人脸照（R-05 敏感影像）。
   */
  const displayName = session.data?.realName ?? '同学';

  return (
    <PageShell title="个人中心" back tabbar layout="tab">
      <div className="profile-card">
        <div className="profile-card__row">
          <span className="profile-card__avatar">{displayName.slice(0, 1)}</span>
          <div className="profile-card__body">
            <span className="profile-card__name">{displayName}</span>
            <span className="profile-card__meta">
              {phoneMasked ? `${phoneMasked} · 微信已绑定` : '已通过微信授权登录'}
            </span>
          </div>
        </div>
        {latest ? (
          <span className="pill profile-card__tag">
            {latest.enrollTypeName} · {latest.batchName}
          </span>
        ) : null}
      </div>

      <ListCard title="账号信息">
        {/* 「姓名」这一行只放真实姓名：没有就破折号，不拿「同学」冒充姓名 */}
        <ListRow
          label="姓名"
          value={session.data?.realName ?? '—'}
          actionText="修改"
          onAction={() => navigate('/profile/complete?edit=1&next=/me')}
        />
        <ListRow
          label="手机号"
          value={phoneMasked ?? '未绑定'}
          actionText="修改"
          onAction={() => setPhoneModalOpen(true)}
        />
      </ListCard>

      <ListCard title="快捷入口">
        <ListRow
          label="报名记录"
          onClick={() => navigate(recentId ? `/registration/${recentId}` : '/registration/list')}
        />
        <ListRow label="缴费指引" onClick={() => navigate('/payment/guide')} />
        <ListRow label="教材领取" onClick={() => navigate('/logistics/track')} />
        <ListRow label="消息中心" onClick={() => navigate('/inbox')} />
        <ListRow label="帮助中心 · 常见问题" onClick={() => navigate('/help')} />
        <ListRow label="证书查询指引" onClick={() => navigate('/help/certificate')} />
        <ListRow label="联系客服（企业微信）" onClick={() => navigate('/help/service')} />
      </ListCard>

      <ChangePhoneModal
        visible={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        onDone={() => {
          // 改绑成功后让会话缓存失效，资料卡的脱敏号立即更新
          void queryClient.invalidateQueries({ queryKey: h5QueryKeys.session });
        }}
      />
    </PageShell>
  );
}
